import * as React from 'react';
import * as RechartsPrimitive from 'recharts';
import { cn } from '@/lib/utils';

export type ChartConfig = Record<
	string,
	{
		label?: React.ReactNode;
		icon?: React.ComponentType;
	} & ({ color?: string; theme?: never } | { color?: never; theme: Record<string, string> })
>;

const ChartContext = React.createContext<{ config: ChartConfig } | null>(null);

function useChart() {
	const context = React.useContext(ChartContext);
	if (!context) {
		throw new Error('useChart must be used within a <ChartContainer />');
	}
	return context;
}

function ChartContainer({
	id,
	className,
	children,
	config,
	...props
}: React.ComponentProps<'div'> & {
	config: ChartConfig;
	children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children'];
}) {
	const uniqueId = React.useId();
	const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;

	return (
		<ChartContext.Provider value={{ config }}>
			<div
				data-chart={chartId}
				className={cn(
					"[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 flex aspect-auto justify-center text-xs [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-surface]:outline-hidden",
					className,
				)}
				{...props}
			>
				<ChartStyle id={chartId} config={config} />
				<RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
			</div>
		</ChartContext.Provider>
	);
}

function ChartStyle({ id, config }: { id: string; config: ChartConfig }) {
	const colorConfig = Object.entries(config).filter(([, itemConfig]) => itemConfig.theme || itemConfig.color);

	if (!colorConfig.length) {
		return null;
	}

	return (
		<style
			dangerouslySetInnerHTML={{
				__html: Object.entries(config)
					.filter(([, itemConfig]) => itemConfig.color)
					.map(
						([key, itemConfig]) =>
							`[data-chart=${id}] .color-${key} { color: ${itemConfig.color}; } [data-chart=${id}] .fill-${key} { fill: ${itemConfig.color}; } [data-chart=${id}] .stroke-${key} { stroke: ${itemConfig.color}; }`,
					)
					.join('\n'),
			}}
		/>
	);
}

const ChartTooltip = RechartsPrimitive.Tooltip;

type TooltipPayloadItem = {
	name?: string;
	dataKey?: string | number;
	value?: number | string;
	payload?: Record<string, unknown>;
};

function ChartTooltipContent({
	active,
	payload,
	label,
	hideLabel = false,
	className,
	formatter,
	labelFormatter,
}: {
	active?: boolean;
	payload?: TooltipPayloadItem[];
	label?: string | number;
	hideLabel?: boolean;
	className?: string;
	formatter?: (value: number | string, name: string, item: TooltipPayloadItem, index: number, payload: TooltipPayloadItem[]) => React.ReactNode;
	labelFormatter?: (label: string | number, payload: TooltipPayloadItem[]) => React.ReactNode;
}) {
	const { config } = useChart();

	if (!active || !payload?.length) {
		return null;
	}

	return (
		<div className={cn('border-border/50 bg-background grid min-w-[8rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl', className)}>
			{!hideLabel && label != null && (
				<div className='font-medium'>{labelFormatter ? labelFormatter(label, payload) : label}</div>
			)}
			<div className='grid gap-1'>
				{payload.map((item, index) => {
					const key = `${item.name ?? item.dataKey ?? 'value'}`;
					const itemConfig = config[key];
					const value = formatter
						? formatter(item.value ?? 0, key, item, index, payload)
						: item.value?.toLocaleString('ru-RU');

					return (
						<div key={String(item.dataKey)} className='flex items-center justify-between gap-3'>
							<span className='text-muted-foreground'>{itemConfig?.label ?? key}</span>
							<span className='font-mono font-medium tabular-nums'>{value}</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}

export { ChartContainer, ChartTooltip, ChartTooltipContent };
